import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Alert, Button, DatePicker, Modal, Space, Tag, Tooltip, message } from 'antd'
import { CheckCircleOutlined, InboxOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import { supplyRequest } from './api'

type AcceptancePreparation = {
  canStart: boolean
  canComplete: boolean
  disabled: boolean
  confirmed: boolean
  content: (saving: boolean, close: () => void, hasDelivery: boolean) => React.ReactNode
  confirm: () => Promise<boolean>
  onSelectDocument: (id: string) => void
  otherPending?: Array<{ id: string; original?: { document_number?: string; supplier?: string } }>
}

export default function DeliveryAcceptance({
  reviewId,
  revision,
  autoOpen,
  inline = false,
  deliveryOnly = false,
  triggerless = false,
  onAccepted,
  onAcceptanceState,
  preparation,
}: {
  reviewId: string
  revision: string
  autoOpen: number
  inline?: boolean
  deliveryOnly?: boolean
  triggerless?: boolean
  onAccepted?: () => void
  onAcceptanceState?: (accepted: boolean) => void
  preparation?: AcceptancePreparation
}) {
  const navigate = useNavigate()
  const openedToken = useRef(0)
  const confirming = useRef(false)
  const loadSequence = useRef(0)
  const [intake, setIntake] = useState<any>(null)
  const [loaded, setLoaded] = useState(false)
  const [open, setOpen] = useState(false)
  const [arrivedAt, setArrivedAt] = useState<Dayjs | null>(dayjs().startOf('day'))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [pendingDocuments, setPendingDocuments] = useState<
    Array<{ id: string; original?: { document_number?: string; supplier?: string } }>
  >([])
  const load = useCallback(
    async () => (await supplyRequest(`/api/supply/reviews/${reviewId}/delivery`)).data,
    [reviewId],
  )
  useEffect(() => {
    let active = true
    const sequence = ++loadSequence.current
    load()
      .then(data => {
        if (!active || sequence !== loadSequence.current || confirming.current) return
        setIntake(data)
        setLoaded(true)
        setError('')
        onAcceptanceState?.(data?.status === 'checked')
        if (autoOpen > openedToken.current && triggerless) {
          openedToken.current = autoOpen
          setArrivedAt(data?.arrived_at ? dayjs(data.arrived_at).startOf('day') : dayjs().startOf('day'))
          setPendingDocuments(preparation?.otherPending || [])
          setOpen(true)
        } else if (autoOpen > openedToken.current && data?.can_confirm && data.status !== 'checked') {
          openedToken.current = autoOpen
          setArrivedAt(dayjs().startOf('day'))
          setOpen(true)
        }
      })
      .catch(cause => {
        if (active && sequence === loadSequence.current) setError(cause.message)
      })
    return () => {
      active = false
    }
  }, [load, revision, autoOpen])
  const busy = saving || !!preparation?.disabled
  const canStart = preparation
    ? preparation.canStart && !preparation.disabled
    : intake?.can_confirm === true
  const canComplete = preparation
    ? preparation.canComplete && !preparation.disabled
    : intake?.can_confirm === true
  const close = () => {
    if (!confirming.current) setOpen(false)
  }
  const confirm = async () => {
    if (
      !canComplete ||
      (intake && (!arrivedAt || !arrivedAt.isValid() || arrivedAt.isAfter(dayjs(), 'day'))) ||
      confirming.current
    )
      return
    confirming.current = true
    loadSequence.current++
    setSaving(true)
    setError('')
    setPendingDocuments([])
    try {
      if (preparation && !(await preparation.confirm())) return
      if (!intake) {
        setOpen(false)
        onAccepted?.()
        return
      }
      const [documents, currentIntake] = await Promise.all([
        supplyRequest(`/api/supply/reviews/${reviewId}/documents`),
        load(),
      ])
      setIntake(currentIntake)
      const pending = documents.data.filter(document => document.status !== 'confirmed')
      if (!currentIntake?.can_confirm || pending.length) {
        setPendingDocuments(pending)
        throw new Error(
          pending.length
            ? 'В поставке есть другие непроверенные накладные. Откройте их и завершите проверку перед приёмкой.'
            : currentIntake?.confirmation_problem || 'Поставка пока не готова к приёмке',
        )
      }
      const result = await supplyRequest(`/api/supply/reviews/${reviewId}/delivery/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          arrived_at: arrivedAt!.startOf('day').toISOString(),
          versions: Object.fromEntries(
            documents.data.map(document => [document.id, document.version]),
          ),
        }),
      })
      loadSequence.current++
      setIntake({ ...currentIntake, ...result.data, status: 'checked' })
      onAcceptanceState?.(true)
      setOpen(false)
      message.success('Приёмка подтверждена в ERP')
      onAccepted?.()
    } catch (cause: any) {
      setError(cause.message || 'Не удалось подтвердить приёмку')
    } finally {
      loadSequence.current++
      confirming.current = false
      setSaving(false)
    }
  }
  if (!intake && (!loaded || !preparation))
    return error ? (
      <Alert
        type="warning"
        message="Не удалось проверить состояние приёмки"
        description={error}
        action={
          <Button
            onClick={() =>
              load()
                .then(data => {
                  setIntake(data)
                  setLoaded(true)
                  setError('')
                })
                .catch(cause => setError(cause.message))
            }
          >
            Повторить
          </Button>
        }
      />
    ) : null
  const accepted = intake?.status === 'checked' && (!preparation || preparation.confirmed)
  if (!intake && !triggerless && (deliveryOnly || preparation?.confirmed)) return null
  const actionLabel = intake ? 'Подтвердить приёмку' : 'Сохранить проверку'
  return (
    <div className={`delivery-acceptance${inline ? ' delivery-acceptance--inline' : ''}`}>
      {!triggerless && (accepted ? (
        <Tag color="success" icon={<CheckCircleOutlined />}>
          Приёмка подтверждена · {dayjs(intake.arrived_at).format('DD.MM.YYYY')}
        </Tag>
      ) : (
        (intake?.can_confirm || inline) && (
          <Tooltip
            title={
              !canStart
                ? preparation
                  ? 'Сначала подтвердите все товары в таблице'
                  : intake.confirmation_problem
                : undefined
            }
          >
            <Button
              disabled={!canStart || busy}
              type="primary"
              icon={<InboxOutlined />}
              onClick={() => {
                setError('')
                setPendingDocuments([])
                setArrivedAt(
                  intake?.arrived_at
                    ? dayjs(intake.arrived_at).startOf('day')
                    : dayjs().startOf('day'),
                )
                setOpen(true)
              }}
            >
              {actionLabel}
            </Button>
          </Tooltip>
        )
      ))}
      {!inline && intake && (
        <Button
          onClick={() =>
            navigate(
              `/warehouse/deliveries?date=${
                intake.snapshot.planned_date || dayjs(intake.arrived_at).format('YYYY-MM-DD')
              }`,
            )
          }
        >
          Календарь поставок
        </Button>
      )}
      {intake?.document_count > 0 &&
        intake.processing_state &&
        intake.processing_state !== 'ready' && (
          <Alert type="warning" showIcon message={intake.confirmation_problem} />
        )}
      <Modal
        className={preparation ? 'supply-check__completion' : undefined}
        title={preparation ? 'Завершение проверки накладной' : 'Подтверждение приёмки'}
        open={open}
        width={preparation ? 800 : 520}
        onCancel={close}
        closable={!busy}
        maskClosable={!busy}
        keyboard={!busy}
        footer={
          <Space wrap>
            <Button disabled={busy} onClick={close}>
              Вернуться к накладной
            </Button>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              loading={saving}
              disabled={
                !canComplete ||
                busy ||
                (!!intake && !!preparation?.otherPending?.length) ||
                (!!intake &&
                  (!arrivedAt || !arrivedAt.isValid() || arrivedAt.isAfter(dayjs(), 'day')))
              }
              onClick={confirm}
            >
              {actionLabel}
            </Button>
          </Space>
        }
      >
        {preparation ? (
          preparation.content(saving, close, !!intake)
        ) : (
          <>
            <p>
              <strong>{intake.snapshot.supplier || 'Поставка'}</strong>
            </p>
            <Alert
              showIcon
              type="success"
              message={`Все накладные проверены: ${intake.checked_count} из ${intake.document_count}`}
            />
          </>
        )}
        {intake && (
          <>
            <label className="delivery-acceptance__date">
              Когда товар фактически поступил на склад?
              <DatePicker
                aria-label="Дата приёмки"
                format="DD.MM.YYYY"
                value={arrivedAt}
                onChange={setArrivedAt}
                disabled={busy}
                disabledDate={date => date.startOf('day').isAfter(dayjs().startOf('day'))}
                style={{ width: '100%' }}
              />
            </label>
            <p className="delivery-calendar__muted">
              Можно указать прошлую дату. Приёмка попадёт в статистику календаря.
            </p>
            <Alert
              type="info"
              showIcon
              message="Проверка и приёмка сохранятся в ERP"
              description="Отправка и проведение в 1С выполняются отдельно."
            />
          </>
        )}
        {error && <Alert type="error" showIcon message={error} style={{ marginTop: 12 }} />}
        {!!(preparation?.otherPending?.length || pendingDocuments.length) && preparation && (
          <Alert type="warning" showIcon message="Сначала проверьте остальные накладные поставки" description={<Space wrap>
            {(preparation.otherPending?.length ? preparation.otherPending : pendingDocuments).map(document => (
              <Button
                key={document.id}
                type="link"
                disabled={busy}
                onClick={() => {
                  setOpen(false)
                  preparation.onSelectDocument(document.id)
                }}
              >
                Проверить накладную № {document.original?.document_number || 'без номера'}
                {document.original?.supplier ? ` · ${document.original.supplier}` : ''}
              </Button>
            ))}
          </Space>} style={{ marginTop: 12 }} />
        )}
      </Modal>
    </div>
  )
}
